
import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

type Categoria = {
  id: string;
  nome: string;
  tipo: string;
};

const CategoriasTabContent = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<null | Categoria>(null);
  const [categorias, setCategorias] = useState([
    { id: "1", nome: "Vendas", tipo: "receita" },
    { id: "2", nome: "Comissões", tipo: "receita" },
    { id: "3", nome: "Consultorias", tipo: "receita" },
    { id: "4", nome: "Manutenção", tipo: "receita" },
    { id: "5", nome: "Fornecedores", tipo: "despesa" },
    { id: "6", nome: "Aluguel", tipo: "despesa" },
    { id: "7", nome: "Salários", tipo: "despesa" },
    { id: "8", nome: "Impostos", tipo: "despesa" },
  ]);

  const handleCategoryCreate = () => {
    setEditingCategory({ id: "", nome: "", tipo: "receita" });
    setDialogOpen(true);
  };
  
  const handleCategoryEdit = (categoria: Categoria) => {
    setEditingCategory(categoria);
    setDialogOpen(true);
  };
  
  const handleCategorySave = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCategory) return;

    if (editingCategory.id) {
      setCategorias(categorias.map(cat => 
        cat.id === editingCategory.id ? editingCategory : cat
      ));
      toast.success("Categoria atualizada com sucesso!");
    } else {
      const newCategory = {
        ...editingCategory,
        id: Math.random().toString(36).substr(2, 9)
      };
      setCategorias([...categorias, newCategory]);
      toast.success("Categoria criada com sucesso!");
    }
    
    setDialogOpen(false);
    setEditingCategory(null);
  };
  
  const handleCategoryDelete = (id: string) => {
    setCategorias(categorias.filter(cat => cat.id !== id));
    toast.success("Categoria excluída com sucesso!");
  };

  return (
    <>
      <Card className="shadow-sm border-gray-100">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">Categorias de Receitas e Despesas</CardTitle>
            <CardDescription>
              Gerencie as categorias para classificação de transações financeiras
            </CardDescription>
          </div>
          <Button onClick={handleCategoryCreate} className="bg-blue-500 hover:bg-blue-600 text-white shadow-sm">
            <Plus className="h-4 w-4 mr-2" />
            Nova Categoria
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-gray-100 overflow-hidden">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categorias.map((categoria) => (
                  <TableRow key={categoria.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{categoria.nome}</TableCell>
                    <TableCell>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        categoria.tipo === 'receita' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {categoria.tipo === 'receita' ? 'Receita' : 'Despesa'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => handleCategoryEdit(categoria)}
                          className="h-8 w-8 rounded-full hover:bg-gray-100"
                        >
                          <Edit className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          onClick={() => handleCategoryDelete(categoria.id)}
                          className="h-8 w-8 rounded-full hover:bg-red-50 text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {editingCategory?.id ? "Editar Categoria" : "Nova Categoria"}
            </DialogTitle>
            <DialogDescription>
              {editingCategory?.id 
                ? "Edite os detalhes da categoria selecionada" 
                : "Adicione uma nova categoria para classificar transações"
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCategorySave}>
            <div className="grid gap-4 py-4">
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="category-name" className="text-gray-700">Nome da Categoria</Label>
                <Input 
                  id="category-name" 
                  value={editingCategory?.nome || ""} 
                  onChange={(e) => setEditingCategory({...editingCategory!, nome: e.target.value})}
                  placeholder="Ex: Vendas, Fornecedores, etc."
                  className="border-gray-200 focus:border-blue-300 focus:ring focus:ring-blue-100"
                  required
                />
              </div>
              
              <div className="grid w-full items-center gap-1.5">
                <Label className="text-gray-700">Tipo</Label>
                <div className="flex space-x-4">
                  <Label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="tipo" 
                      value="receita"
                      checked={editingCategory?.tipo === "receita"}
                      onChange={() => setEditingCategory({...editingCategory!, tipo: "receita"})}
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-300"
                    />
                    <span>Receita</span>
                  </Label>
                  <Label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="tipo" 
                      value="despesa"
                      checked={editingCategory?.tipo === "despesa"}
                      onChange={() => setEditingCategory({...editingCategory!, tipo: "despesa"})}
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-300"
                    />
                    <span>Despesa</span>
                  </Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-gray-200"
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                {editingCategory?.id ? "Atualizar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CategoriasTabContent;
